import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { AdPlacement, PublicAffiliate } from '../api/types';
import { useSite } from '../site/SiteProvider';

/**
 * 広告の枠（doc/ADS.md §2）。`FEATURE_ADS` のサイトで、その場所に出すものがあるときだけ描く。
 *
 * - **どの枠にも「広告」と書く。** アフィリエイトのリンクにも「PR」を付ける（景品表示法のステルスマーケティング規制）。
 * - アフィリエイトは管理メニューで入れた URL だけでリンクを組む（ASP の HTML は差し込まない）。`rel="sponsored"`。
 * - AdSense は場所ごとの広告ユニット（`ADSENSE_SLOTS`）がある場所にだけ置く。無い場所は自動広告に任せる。
 * - 置く場所は、読み終えたところ（結果の下・一覧の下・読み物の下）。**設問の画面には置かない**（誤クリックを誘う配置は AdSense のポリシー違反）。
 * - 利用者が作った診断の入口・結果は、サーバが「出してよい」と言ったときだけ（`allowed`。受けた回数と通報。doc/ADS.md §5）。
 */
const MAX_AFFILIATE = 3;

export function AdSlot({ placement, allowed = true }: { readonly placement: AdPlacement; readonly allowed?: boolean }) {
  const { ads } = useSite();
  const { pathname } = useLocation();
  if (!ads || !allowed) return null;

  const slot = ads.adsense?.slots[placement];
  const items = ads.affiliate.filter((item) => item.placements.includes(placement)).slice(0, MAX_AFFILIATE);
  if (!slot && items.length === 0) return null;

  return (
    <aside className="ad" aria-label="広告">
      <p className="ad__label">広告</p>
      {items.length > 0 && (
        <ul className="ad__list">
          {items.map((item) => <AffiliateCard key={item.id} item={item} />)}
        </ul>
      )}
      {/* ページが変わったら別のユニットとして描き直す（SPA なので、同じ要素に 2 度は push できない） */}
      {slot && ads.adsense && <AdsenseUnit key={pathname} client={ads.adsense.client} slot={slot} />}
    </aside>
  );
}

function AffiliateCard({ item }: { readonly item: PublicAffiliate }) {
  return (
    <li className="ad__item">
      <a className="ad__link" href={item.url} target="_blank" rel="sponsored noopener">
        {item.imageUrl && <img className="ad__image" src={item.imageUrl} alt="" loading="lazy" />}
        <span className="ad__body">
          <span className="ad__pr">PR</span>
          <span className="ad__title">{item.title}</span>
          {item.text && <span className="ad__text">{item.text}</span>}
        </span>
      </a>
      {/* 表示を数える 1×1 の画像（A8.net などが広告タグに添えるもの） */}
      {item.pixelUrl && <img className="ad__pixel" src={item.pixelUrl} width={1} height={1} alt="" loading="lazy" />}
    </li>
  );
}

/** AdSense の広告ユニット 1 つ。本番は index.html にコードが入っている。無ければ（開発中）ここで読む。 */
function AdsenseUnit({ client, slot }: { readonly client: string; readonly slot: string }) {
  const pushed = useRef(false);

  useEffect(() => {
    if (pushed.current) return; // StrictMode の 2 度目の実行で同じ枠に push しない
    pushed.current = true;
    ensureAdsenseScript(client);
    try {
      (window.adsbygoogle = window.adsbygoogle ?? []).push({});
    } catch {
      // 広告が埋まらなくても画面は止めない
    }
  }, [client]);

  return (
    <ins
      className="adsbygoogle ad__adsense"
      style={{ display: 'block' }}
      data-ad-client={client}
      data-ad-slot={slot}
      data-ad-format="auto"
      data-full-width-responsive="true"
    />
  );
}

function ensureAdsenseScript(client: string): void {
  if (document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]')) return;
  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
  document.head.appendChild(script);
}
